import { NeutralIcon } from '@assets/icons/engagement';
import { For, HStack, Icon, Text, VStack } from '@chakra-ui/react';
import TargetId from '@components/map/popup/infos/targets/TargetId';
import { Tooltip } from '@components/ui/tooltip';
import { useAppSelector } from '@hooks/redux.hooks';
import { useSelectorWithReplayMode } from '@hooks/useSelectorWithReplayMode';
import { selectActiveOrganization } from '@redux/authent/authent.selectors';
import { makeSelectPlatformsNamesByCode } from '@redux/config/config.selectors';
import { selectTargetByDisplayId } from '@redux/situation/situation.selectors';
import { getPlatformsCodesFromMarks } from '@utils/common.utils';
import { getTargetDisplayedId, getTargetIcon } from '@utils/target.utils';
import { useMemo } from 'react';
import { shallowEqual } from 'react-redux';

import { Target } from '@/types/c2/c2.types';
import { TargetLocation } from '@/types/sensor/sensor.types';

type Props = {
  targetId: string;
};

export default function EngagementTargetHeader({ targetId }: Readonly<Props>) {
  const organization = useAppSelector(selectActiveOrganization);
  const target: Target<TargetLocation> | undefined = useSelectorWithReplayMode(selectTargetByDisplayId, targetId);
  const platformCodes = getPlatformsCodesFromMarks(target?.marks ?? []);
  const selectPlatformNames = useMemo(makeSelectPlatformsNamesByCode, []);
  const platforms = useAppSelector((state) => selectPlatformNames(state, platformCodes), shallowEqual);
  const model = target?.collaborativeData?.model;
  const modelLabel = model !== undefined ? ` ${model}` : '';
  const displayedId = target ? getTargetDisplayedId(target) : targetId;

  return (
    <HStack width="100%" height="120px" gap={2} backgroundColor="neutral.700">
      <Icon as={target ? getTargetIcon(target, true, organization) : NeutralIcon} width="86px" height="86px" />
      <VStack gap={0.5} alignItems="start">
        <TargetId label={`${displayedId}${modelLabel}`} isTechnical={!target?.displayId} />
        <HStack maxWidth="100%" gap={0.5}>
          <For each={platforms}>
            {(platform) => (
              <Tooltip key={platform} content={platform} openDelay={200}>
                <Text
                  paddingX={0.25}
                  backgroundColor="neutral.600"
                  textTransform="uppercase"
                  textStyle="md"
                  fontWeight="medium"
                  color="neutral.200"
                >
                  {platform.slice(0, 4)}
                </Text>
              </Tooltip>
            )}
          </For>
        </HStack>
      </VStack>
    </HStack>
  );
}
